import { useState, useEffect, useMemo, useRef } from 'react'
import { Loader2, Check, Info, X } from 'lucide-react'
import { createApp, checkAppName, type App, type NameCheckResult } from '../lib/api'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => any>(fn: T, ms: number) {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), ms)
  }
}

function sanitizeName(displayName: string): string {
  return displayName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars except spaces and hyphens
    .replace(/\s+/g, '-')         // Replace spaces with hyphens
    .replace(/-+/g, '-')          // Collapse multiple hyphens
    .replace(/^-|-$/g, '')        // Remove leading/trailing hyphens
    .slice(0, 50)                 // Max length
}

const accessLevelConfig = {
  personal: { label: 'Personal', description: 'Only you can access' },
  specific: { label: 'Specific Users', description: 'Share with selected users' },
  platform: { label: 'Platform', description: 'Any authenticated user' },
}

interface CreateAppModalProps {
  onClose: () => void
  onCreated: (app: App) => void
}

export default function CreateAppModal({ onClose, onCreated }: CreateAppModalProps) {
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [autoBuild, setAutoBuild] = useState(false)
  const [accessLevel, setAccessLevel] = useState('personal')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nameCheck, setNameCheck] = useState<NameCheckResult | null>(null)
  const [checkingName, setCheckingName] = useState(false)

  // Track the current request to prevent race conditions
  const currentRequestRef = useRef<number>(0)

  // Auto-generate app name from display name
  const appName = sanitizeName(displayName)

  // Debounced name check with race condition protection
  const debouncedCheckName = useMemo(
    () => debounce(async (value: string) => {
      if (value.length < 3) {
        setNameCheck(null)
        return
      }

      // Increment request counter to track this specific request
      const requestId = ++currentRequestRef.current

      setCheckingName(true)
      try {
        const result = await checkAppName(value)
        // Only update state if this is still the most recent request
        if (requestId === currentRequestRef.current) {
          setNameCheck(result)
        }
      } catch {
        // Only update state if this is still the most recent request
        if (requestId === currentRequestRef.current) {
          setNameCheck(null)
        }
      } finally {
        // Only update loading state if this is still the most recent request
        if (requestId === currentRequestRef.current) {
          setCheckingName(false)
        }
      }
    }, 300),
    []
  )

  // Check name whenever it changes
  useEffect(() => {
    if (appName.length >= 3) {
      debouncedCheckName(appName)
    } else {
      setNameCheck(null)
    }
  }, [appName, debouncedCheckName])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!appName || appName.length < 3 || (nameCheck && !nameCheck.available)) return

    setCreating(true)
    setError(null)

    try {
      const createdApp = await createApp({
        name: appName,
        display_name: displayName || undefined,
        description: description || undefined,
        runtime: 'auto',  // Auto-detected by app-runner
        access_level: accessLevel,
        auto_build: autoBuild,
      })
      onCreated(createdApp)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create app')
    } finally {
      setCreating(false)
    }
  }

  const isNameValid = appName.length >= 3 && nameCheck?.available

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-6">Create New App</h2>

          <form onSubmit={handleCreate} className="space-y-5">
            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium mb-1.5">App Name *</label>
              <div className="relative">
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="My Awesome App"
                  required
                  minLength={3}
                  maxLength={60}
                  autoFocus
                  className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingName ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : nameCheck?.available ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : nameCheck && !nameCheck.available ? (
                    <X className="w-4 h-4 text-destructive" />
                  ) : null}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {nameCheck?.available === false ? (
                  <span className="text-destructive">
                    Name "{appName}" is taken. Try a different name.
                  </span>
                ) : appName.length >= 3 ? (
                  <>Your app will be at <code className="bg-secondary px-1 rounded">/a/{appName}/</code></>
                ) : (
                  'Enter at least 3 characters'
                )}
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium mb-1.5">
                Description
                <span className="relative group/tip">
                  <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  <span className="absolute left-0 top-full mt-1.5 w-64 px-3 py-2 rounded-md border border-border bg-card text-xs text-card-foreground leading-relaxed shadow-md opacity-0 pointer-events-none group-hover/tip:opacity-100 group-hover/tip:pointer-events-auto transition-opacity duration-150 z-50">
                    A short summary for people browsing your app. If you want Claude to start coding immediately from this description, turn on auto-build below.
                  </span>
                </span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Weekly newsletter signup page for the marketing team"
                rows={4}
                className="w-full min-h-[112px] px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-y"
              />
            </div>

            {/* Auto-build toggle */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">Start building automatically</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Claude will begin from your description right after app creation
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={autoBuild}
                onClick={() => setAutoBuild(!autoBuild)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-border transition-colors ${
                  autoBuild ? 'bg-primary' : 'bg-secondary'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                    autoBuild ? 'translate-x-[18px]' : 'translate-x-[3px]'
                  }`}
                />
              </button>
            </div>

            {/* Access Level */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Access Level</label>
              <select
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {Object.entries(accessLevelConfig).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label} - {config.description}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !isNameValid}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create App'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
